import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { FileText, Download, Calendar, TrendingUp, Package, Truck, Filter } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function TransferCostReport() {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // Ayın ilk günü
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const res = await api.get(`/reports/transfer-cost?${params.toString()}`);
      setReport(res.data);
    } catch (error) {
      toast.error('Rapor yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    fetchReport();
  };

  const handleDownloadPDF = () => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${backendUrl}/api/reports/transfer-cost/pdf?${params.toString()}`, '_blank');
    toast.success('PDF indiriliyor...');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'et': 'bg-red-100 text-red-700',
      'sebze': 'bg-green-100 text-green-700',
      'meyve': 'bg-yellow-100 text-yellow-700',
      'sut_urunleri': 'bg-blue-100 text-blue-700',
      'icecek': 'bg-purple-100 text-purple-700',
      'baharat': 'bg-orange-100 text-orange-700',
      'gida': 'bg-amber-100 text-amber-700',
    };
    return colors[category?.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="transfer-cost-report">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Sevkiyat Maliyet Raporu</h1>
            <p className="text-muted-foreground">Mağazaya gönderilen ürünlerin maliyet analizi</p>
          </div>
          <Button onClick={handleDownloadPDF} data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-2" />
            PDF İndir
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label>Başlangıç Tarihi</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-40"
                  data-testid="start-date-input"
                />
              </div>
              <div>
                <Label>Bitiş Tarihi</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-40"
                  data-testid="end-date-input"
                />
              </div>
              <Button onClick={handleFilter} data-testid="filter-btn">
                <Filter className="w-4 h-4 mr-2" />
                Filtrele
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setTimeout(fetchReport, 100);
                }}
              >
                Temizle
              </Button>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : report && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-red-500/20 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-xs text-red-600 uppercase font-medium">Toplam Maliyet</p>
                      <p className="text-2xl font-bold text-red-700">{formatCurrency(report.summary.total_cost)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-500/20 rounded-lg">
                      <Truck className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-blue-600 uppercase font-medium">Toplam Sevkiyat</p>
                      <p className="text-2xl font-bold text-blue-700">{report.summary.total_transfers} adet</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-green-500/20 rounded-lg">
                      <Package className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-green-600 uppercase font-medium">Toplam Miktar</p>
                      <p className="text-2xl font-bold text-green-700">{report.summary.total_quantity?.toFixed(2)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-500/20 rounded-lg">
                      <FileText className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-purple-600 uppercase font-medium">Ortalama Maliyet</p>
                      <p className="text-2xl font-bold text-purple-700">{formatCurrency(report.summary.average_cost_per_transfer)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Category Summary */}
            {report.category_summary && report.category_summary.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Kategori Bazında Maliyet
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {report.category_summary.map((cat, idx) => (
                      <div key={idx} className="p-3 rounded-lg border bg-muted/30">
                        <Badge className={getCategoryColor(cat.category)}>{cat.category}</Badge>
                        <p className="text-lg font-bold mt-2">{formatCurrency(cat.cost)}</p>
                        <p className="text-xs text-muted-foreground">{cat.count} sevkiyat</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Daily Summary */}
            {report.daily_summary && report.daily_summary.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Günlük Özet
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead className="text-center">Sevkiyat Sayısı</TableHead>
                        <TableHead className="text-right">Maliyet</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.daily_summary.slice(0, 10).map((day, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{formatDate(day.date)}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{day.count} adet</Badge>
                          </TableCell>
                          <TableCell className="text-right font-bold text-red-600">
                            {formatCurrency(day.cost)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Detail Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Sevkiyat Detayları
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.transfers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Truck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Bu tarih aralığında sevkiyat bulunamadı</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Saat</TableHead>
                        <TableHead>Ürün</TableHead>
                        <TableHead>Kategori</TableHead>
                        <TableHead className="text-right">Miktar</TableHead>
                        <TableHead className="text-right">Birim Fiyat</TableHead>
                        <TableHead className="text-right">Maliyet</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.transfers.map((transfer) => (
                        <TableRow key={transfer.id} data-testid={`transfer-row-${transfer.id}`}>
                          <TableCell className="font-medium">{transfer.date}</TableCell>
                          <TableCell className="text-muted-foreground">{transfer.time}</TableCell>
                          <TableCell>{transfer.product_name}</TableCell>
                          <TableCell>
                            <Badge className={getCategoryColor(transfer.category)}>
                              {transfer.category}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {transfer.quantity} {transfer.unit}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {formatCurrency(transfer.unit_price)}
                          </TableCell>
                          <TableCell className="text-right font-bold text-red-600">
                            {formatCurrency(transfer.total_cost)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Total Row */}
                      <TableRow className="bg-muted/50 font-bold">
                        <TableCell colSpan={6}>TOPLAM</TableCell>
                        <TableCell className="text-right text-red-700 text-lg">
                          {formatCurrency(report.summary.total_cost)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
